import type { SelectItem } from 'common/models';
import { useGetUsersQuery } from 'pages/settings/users/queries';
import { useCallback, useMemo } from 'react';

import { getFullName } from './contactPersonHelpers';
import { useCustomTranslation } from './translationHelper';

export const useUsers = (enabled = true) => {
  const usersQuery = useGetUsersQuery(enabled);

  const { tCommon } = useCustomTranslation();

  const filterDisabledUsersForForm = useMemo(
    () =>
      (originalUsers?: string[], values?: string[]): SelectItem[] => {
        if (!originalUsers) {
          return (
            usersQuery.data
              ?.filter((x) => !x.isDisabled)
              .map((x) => ({ id: x.id, name: getFullName(x) })) || []
          );
        } else {
          return (
            usersQuery.data
              ?.filter((x) => !x.isDisabled || originalUsers.includes(x.id))
              .map((x) => ({
                id: x.id,
                name: getFullName(x),
                additionalInfo: x.isDisabled ? tCommon('disabled') : undefined,
                disabled: x.isDisabled && !values?.includes(x.id)
              })) || []
          );
        }
      },
    [usersQuery.data, tCommon]
  );

  const filterDisabledTechniciansForForm = useMemo(
    () =>
      (originalAssignations?: string[], values?: string[]): SelectItem[] => {
        if (!originalAssignations) {
          return (
            usersQuery.data
              ?.filter((x) => !x.isDisabled && x.isTechnician)
              .map((x) => ({ id: x.id, name: getFullName(x) })) || []
          );
        } else {
          return (
            usersQuery.data
              ?.filter(
                (x) => (!x.isDisabled && x.isTechnician) || originalAssignations.includes(x.id)
              )
              .map((x) => ({
                id: x.id,
                name: getFullName(x),
                additionalInfo: x.isDisabled ? tCommon('disabled') : undefined,
                disabled: x.isDisabled && !values?.includes(x.id)
              })) || []
          );
        }
      },
    [usersQuery.data, tCommon]
  );

  const ownersForForm = useMemo(
    () =>
      (originalOwnerId?: string | null, newOwnerId?: string | null): SelectItem[] =>
        usersQuery.data
          ?.filter((x) => (x.isManager && !x.isDisabled) || x.id === originalOwnerId)
          .map((user) => ({
            name: getFullName(user),
            id: user.id,
            additionalInfo: user.isDisabled ? tCommon('disabled') : undefined,
            disabled: user.isDisabled && newOwnerId !== user.id
          })) || [],
    [usersQuery.data, tCommon]
  );

  const orderOwnersForForm = useMemo(
    () =>
      (originalOwnerId?: string | null, newOwnerId?: string | null): SelectItem[] =>
        usersQuery.data
          ?.filter(
            (x) => ((x.isManager || x.canAddOrders) && !x.isDisabled) || x.id === originalOwnerId
          )
          .map((user) => ({
            name: getFullName(user),
            id: user.id,
            additionalInfo: user.isDisabled ? tCommon('disabled') : undefined,
            disabled: user.isDisabled && newOwnerId !== user.id
          })) || [],
    [usersQuery.data, tCommon]
  );

  const interventionOwnersForForm = useMemo(
    () =>
      (originalOwnerId?: string | null, newOwnerId?: string | null): SelectItem[] =>
        usersQuery.data
          ?.filter(
            (x) =>
              ((x.isManager || x.canPlanInterventionsToSelf) && !x.isDisabled) ||
              x.id === originalOwnerId
          )
          .map((user) => ({
            name: getFullName(user),
            id: user.id,
            additionalInfo: user.isDisabled ? tCommon('disabled') : undefined,
            disabled: user.isDisabled && newOwnerId !== user.id
          })) || [],
    [usersQuery.data, tCommon]
  );

  const technicians = useMemo(
    () => usersQuery.data?.filter((x) => x.isTechnician) || [],
    [usersQuery.data]
  );

  const managers = useMemo(
    () => usersQuery.data?.filter((x) => x.isManager) || [],
    [usersQuery.data]
  );

  const schedulableUsers = useMemo(
    () => usersQuery.data?.filter((x) => x.isTechnician || x.isManager) || [],
    [usersQuery.data]
  );

  const managersAndAdmins = useMemo(
    () => usersQuery.data?.filter((x) => x.isManager || x.isAdmin) || [],
    [usersQuery.data]
  );

  const users = useMemo(() => usersQuery.data || [], [usersQuery.data]);

  const taggedDisabledUsers = useMemo(
    () =>
      usersQuery.data?.map((user) => ({
        name: getFullName(user),
        id: user.id,
        additionalInfo: user.isDisabled ? tCommon('disabled') : undefined
      })) || [],
    [usersQuery.data, tCommon]
  );

  // Use to clear deleted users in form data before submitting it
  const filterDeletedUserIds = useCallback(
    (originalIds: string[]) => {
      const existingUserIds = usersQuery.data?.map((user) => user.id) || [];
      const filtered = originalIds.filter((id) => existingUserIds.includes(id));

      return filtered;
    },
    [usersQuery.data]
  );

  // Use to check if user is not deleted
  const isUserExist = (userId: string) => {
    return !!usersQuery.data?.find((x) => x.id === userId);
  };

  return {
    technicians,
    managers,
    schedulableUsers,
    users,
    managersAndAdmins,
    taggedDisabledUsers,
    filterDisabledUsersForForm,
    ownersForForm,
    orderOwnersForForm,
    interventionOwnersForForm,
    filterDisabledTechniciansForForm,
    filterDeletedUserIds,
    isLoading: usersQuery.isLoading,
    error: usersQuery.error,
    isUserExist
  };
};
